/* eslint-disable @reatom/unit-naming-rule */
import {
  action,
  atom,
  onCtxAbort,
  reaction,
  reatomArray,
  withAssign,
} from '@reatom/framework'

import { reatomPathfinderQuoteSubscription } from '@root/features/quote/atoms/reatom-quote-subscription'
import { tradingWalletAtom } from '@root/features/trade/trading'

import { instantSellQuickOptionsAtom } from './reatom-instant-sell'

import type { PairModel } from '@root/entities/pair/atoms'
import type { PathfinderQuoteSubscriptionModel } from '@root/features/quote/atoms/reatom-quote-subscription'
import type { BigNumber } from 'bignumber.js'

type QuoteMeta = {
  amount: BigNumber
  quoteModel: PathfinderQuoteSubscriptionModel
}

export const reatomInstantSellQuote = ({ pair }: { pair: PairModel }) => {
  const modelsAtom = reatomArray<QuoteMeta>([])

  const assetModelAtom = atom((ctx) => {
    const wallet = ctx.spy(tradingWalletAtom)
    if (!wallet) {
      return undefined
    }
    return wallet.asset(pair.quote.address)
  })

  const assetAtom = atom((ctx) => {
    const assetModel = ctx.spy(assetModelAtom)
    if (!assetModel) {
      return undefined
    }
    return ctx.spy(assetModel)
  })

  const subscribeReaction = reaction((ctx) => {
    onCtxAbort(ctx, () => {
      unsubscribeAllAction(ctx)
    })
    const asset = ctx.spy(assetAtom)
    const wallet = ctx.spy(tradingWalletAtom)
    if (!wallet || !asset) {
      return
    }
    const balance = ctx.spy(asset.balance)
    if (balance.isZero()) {
      return
    }

    const options = ctx.spy(instantSellQuickOptionsAtom)
    for (const [_, percents] of options.entries()) {
      const amount = balance.multipliedBy(percents.div(100))

      const quoteModel = reatomPathfinderQuoteSubscription({
        pair,
        amount,
        type: 'sell',
      })
      quoteModel.subscribe(ctx)
      modelsAtom.push(ctx, {
        amount,
        quoteModel,
      })
    }
  })

  const unsubscribeAllAction = action(async (ctx) => {
    const models = ctx.get(modelsAtom)
    for (const model of models) {
      model.quoteModel.unsubscribe(ctx)
    }
    modelsAtom(ctx, [])
  })

  return modelsAtom.pipe(
    withAssign(() => ({
      subscribe: subscribeReaction,
      unsubscribe: unsubscribeAllAction,
    })),
  )
}
